// Guilds, phase 1: membership, roles, recruitment, tag, roster and the Guild
// Boost bank. The Guild War that uses the roster is in shared/guild-war.ts. Numbers follow
// the reference game; the server (server/guilds.ts) enforces every rule here.

/** Coins to found a guild (burned). */
export const GUILD_COST = 250_000;
/** Members a guild may field in the roster. There is no cap on members. */
export const ROSTER_SIZE = 10;
export const TAG_MAX = 3;
export const POSTER_MAX = 80;
export const NAME_MIN = 3;
export const NAME_MAX = 20;
/** After leaving a guild of your own accord, this long before you can join or found another. */
export const LEAVE_COOLDOWN_MS = 24 * 3_600_000;
/** The tag can change once in this long. */
export const TAG_CHANGE_MS = 24 * 3_600_000;
/** Diamonds that fill the bank and start (or extend) the Guild Boost. */
export const BANK_GOAL = 500;
export const BOOST_MS = 7 * 86_400_000;
/** The Guild Boost multiplies kill XP for the trainer and their creatures. */
export const GUILD_BOOST_MULT = 1.1;
/** Donation shortcuts on the Guild page; each gives at most what the bank is missing. */
export const DONATE_SHORTCUTS = [10, 50, 100, 250] as const;
/** Open applications one trainer may have at a time. */
export const MAX_APPS = 5;
/** After withdrawing an application, the same guild can be applied to again only this long after (apply-and-withdraw spam). */
export const REAPPLY_MS = 10 * 60_000;

export type GuildRole = 'owner' | 'coowner' | 'member';
/** open: anyone joins; level: anyone at or above the minimum trainer level joins; closed: apply, owner or co-owners accept. */
export type Recruit = 'open' | 'level' | 'closed';
export const RECRUIT: Record<Recruit, string> = { open: 'Open', level: 'Requirements', closed: 'Closed' };

export const ROLE_NAME: Record<GuildRole, string> = { owner: 'Owner', coowner: 'Co-owner', member: 'Member' };

/** Letters, digits and single spaces, starting and ending with a letter or digit. */
const NAME_RE = /^[A-Za-z0-9](?:[A-Za-z0-9]| (?! ))*[A-Za-z0-9]$/;

export function cleanName(s: unknown): string | null {
  const n = String(s ?? '').trim().replace(/\s+/g, ' ');
  return n.length >= NAME_MIN && n.length <= NAME_MAX && NAME_RE.test(n) ? n : null;
}

/** 1 to 3 letters, stored in capitals. */
export function cleanTag(s: unknown): string | null {
  const t = String(s ?? '').trim().toUpperCase();
  return /^[A-Z]{1,3}$/.test(t) ? t : null;
}

/** Up to 80 characters on one line. */
export function cleanPoster(s: unknown): string {
  return String(s ?? '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, POSTER_MAX);
}

/** What a donation of `want` actually gives: never more than the bank is missing or the donor holds. */
export function donationFor(want: number, bank: number, diamonds: number): number {
  return Math.max(0, Math.min(Math.floor(want) || 0, BANK_GOAL - bank, diamonds));
}

/** A guild in the browse list. */
export interface GuildSummary {
  id: number; name: string; tag: string; size: number; owner: string;
  recruit: Recruit; minLevel: number; poster: string; boosted: boolean;
}

export interface GuildMemberDTO {
  id: number; name: string; level: number; role: GuildRole; joined: number;
  rostered: boolean; online: boolean;
}

/** The guild as its members see it. Applications are listed for the owner and co-owners only. */
export interface GuildDetail extends GuildSummary {
  created: number;
  bank: number; boostUntil: number; tagChangedAt: number;
  /** Rostered member ids, in roster order. */
  roster: number[];
  members: GuildMemberDTO[];
  apps: { id: number; name: string; level: number; at: number }[];
  /** Registered for the daily Guild War. */
  war: boolean;
}

/** Sent as { t: 'guild', view }: your guild (or none), your cooldown and your open applications. */
export interface GuildView {
  guild: GuildDetail | null;
  myRole: GuildRole | null;
  /** You left a guild: no joining or founding before this time. 0 when free. */
  cooldownUntil: number;
  apps: { id: number; name: string; tag: string; at: number }[];
}

/** On the trainer: enough for the chat tag, the Boosts panel and the hunt's XP. */
export interface GuildBrief {
  id: number; name: string; tag: string; role: GuildRole; boostUntil: number;
  /** The Guild War's daily bonus (rostered players of a placed guild): +pct% trainer XP, creature XP and loot until the next war. */
  war?: { pct: number; until: number };
}

export type GuildOp =
  | { k: 'create'; name: string; tag: string; recruit?: Recruit; minLevel?: number; poster?: string }
  /** Join an open guild, or one whose level requirement you meet; apply to a closed one. */
  | { k: 'join'; id: number }
  | { k: 'cancelApp'; id: number }
  | { k: 'accept'; account: number }
  | { k: 'decline'; account: number }
  | { k: 'leave' }
  | { k: 'kick'; account: number }
  /** Owner only: make a member co-owner (true) or back to member (false). */
  | { k: 'coowner'; account: number; on: boolean }
  /** Owner only: hand the guild to another member; you become a co-owner. */
  | { k: 'transfer'; account: number }
  /** Owner only. An unfilled bank goes back to the donors; everyone can join another guild straight away. */
  | { k: 'delete' }
  | { k: 'settings'; recruit?: Recruit; minLevel?: number; poster?: string }
  | { k: 'tag'; tag: string }
  | { k: 'donate'; diamonds: number }
  /** Swap a rostered member out for one who isn't. */
  | { k: 'roster'; out: number; in: number }
  /** Owner only: enter the daily Guild War (on) or stop entering it. */
  | { k: 'warRegister'; on: boolean }
  /** Your own Guild War line-up: 1 to 5 creature ids in order, or [] to fight with your hunt team. */
  | { k: 'warLineup'; ids: number[] };
